import {useDesignerStore} from "@/stores/DesignerStore";
import axios from "axios";
import {useCoreStore} from "@/stores/CoreStore";
import {useQuoteStore} from "@/stores/QuoteStore";
const coreDesignerUrl = process.env.VUE_APP_DESIGNER_URL;
import { toast } from 'vue-sonner'
const requestSecretKey = process.env.VUE_APP_REQUEST_SECRET_KEY; // Your secret key
import CryptoJS from 'crypto-js'; // Import crypto-js

export default class QuoteBuilderService {
    constructor(pageData) {
        const coreStore = useCoreStore();

        this.baseUrl = process.env.VUE_APP_API_URL
        this.pageData = pageData

        // Create Axios instance with base URL
        this.axios = axios.create({
            baseURL: this.baseUrl,
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                'Authorization': 'Bearer ' + coreStore.pageData.apiKey
            },
        });

        // Add a request interceptor to sign every outgoing request
        this.axios.interceptors.request.use(
            (config) => {
                const timestamp = new Date().toISOString();
                let dataToSign = '';

                // For GET requests, the "data" is typically in params or query string,
                // For POST/PUT requests, use the request body.
                if (config.method === 'post' || config.method === 'put') {
                    dataToSign = config.data ? JSON.stringify(config.data) : '';
                } else if (config.method === 'get' && config.params) {
                    // Sort params alphabetically for consistent signature
                    const sortedParams = Object.keys(config.params)
                        .sort()
                        .map(key => `${key}=${config.params[key]}`)
                        .join('&');
                    dataToSign = sortedParams;
                }

                const stringToSign = `${config.method.toUpperCase()}\n${config.url}\n${timestamp}\n${dataToSign}`;
                const signature = CryptoJS.HmacSHA256(stringToSign, requestSecretKey).toString(CryptoJS.enc.Hex);

                // Add signature and timestamp to headers
                config.headers['X-Req-Signature'] = signature;
                config.headers['X-Req-Timestamp'] = timestamp; // Send timestamp for server verification

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }

    async getLeadGenSettings(uuid) {
        const coreStore = useCoreStore();
        let response = await this.axios.post(  '/api/leads/find/' + coreStore.pageData.uuid, {
            domain: window.location.origin.replace(/(^\w+:|^)\/\//, '')
        });

        if (response.data) {
            coreStore.leadGen = response.data.leadGen;
            coreStore.settings = response.data.settings;
            return response.data
        } else {
            return false
        }

    }

    async submitLead(leadData, productDetails = null, flowData = null) {
        const designerStore = useDesignerStore();
        const coreStore = useCoreStore();

        // Build the submission payload
        const submissionPayload = {
            ...leadData,
        };

        // Handle product details - if array with multiple items, send as array; if single item or array with one item, send as single object
        if (productDetails) {
            if (Array.isArray(productDetails)) {
                // If it's an array with more than one item, send as array
                if (productDetails.length > 1) {
                    submissionPayload.product_details = productDetails;
                } else if (productDetails.length === 1) {
                    // If array with single item, send just the item
                    submissionPayload.product_details = productDetails[0];
                }
            } else {
                // If it's a single object, send as is
                submissionPayload.product_details = designerStore.selectedOptions;
            }
        }

        // Include flow data if it exists
        if (flowData && flowData.flowUuid) {
            submissionPayload.flow_data = {
                flow_uuid: flowData.flowUuid,
                flow_id: flowData.flowId,
                completed_steps: flowData.completedSteps,
                form_responses: flowData.formData,
                question_responses: flowData.questionAnswers,
                submitted_at: flowData.submittedAt
            };
        }

        console.log('Submitting lead with payload:', submissionPayload);

        let response = await this.axios.post('/api/leads/submit/' + coreStore.pageData.uuid, submissionPayload)

        if (response.data) {
            toast.success('Quote Request Submitted')
            return response.data;
        }

        return null;
    }


    async getProduct(id = null, editLoad = false) {
        const designerStore = useDesignerStore();
        const coreStore = useCoreStore();

        if (coreStore.pageData.settings){
            coreStore.settings = coreStore.pageData.settings
        }

        // Prioritize the passed 'id' over coreStore.pageData.productId
        this.id = id || coreStore.pageData.productId;

        // Update coreStore.pageData.productId if a new id is provided
        if (id) {
            coreStore.pageData.productId = id;
        }

        if (
            coreStore.pageData.rawData &&
            coreStore.pageData.rawData.product_id
        ) {
            this.id = coreStore.pageData.rawData.product_id
        }

        let response = await this.axios.get( '/api/quote-builder/product/' + this.id);
        let variants = Object.values(response.data.variants)

        designerStore.setProduct(response.data.product)
        designerStore.setVariants(variants)

        if (coreStore.pageData.rawData) {

            let options = coreStore.pageData.rawData;
            designerStore.viewing = true;

            // Find and set the variant first
            let foundVariant = response.data.variants.find(item => item.id == options.variant_id);
            if (foundVariant) {
                designerStore.selectedVariant = foundVariant;
                // Now set the variant explicitly before setting options
                designerStore.setVariant(foundVariant);
            }

            // Then set the rest of the options
            designerStore.setSelectedOptions(options, false) // false to not override variantId
            await this.loadVariant(true) // Load variant configuration with option override
            this.runUpdates()
        } else {
            if (editLoad) {
                response.data.variants.forEach(function (item) {
                    if (item.id == designerStore.selectedOptions.variant_id) {
                        designerStore.selectedVariant = item
                    }
                })

                this.runUpdates()
            } else {
                designerStore.selectedVariant = variants[0];
                this.loadVariant(editLoad)
            }
        }

        designerStore.setLoading(false)

    }


    async loadVariant(noOptionOverride = false) {
        const designerStore = useDesignerStore();
        designerStore.setImages([])
        designerStore.variantLoading = true;


        let response = await this.axios.get(coreDesignerUrl + '/api/designer/v2/config/variant/' + designerStore.selectedVariant.id)

        if (!noOptionOverride) {
            let originalOptions = designerStore.selectedOptions;

            designerStore.setSelectedOptions(response.data.data.default_values)

            if (originalOptions.width) designerStore.selectedOptions.width = originalOptions.width
            if (originalOptions.height) designerStore.selectedOptions.height = originalOptions.height

        }

        designerStore.setTabs(response.data.data.categories)

        designerStore.selectedOptions.view = 'External';

        if (designerStore.designerMode == 1) {
            designerStore.activeTab = response.data.data.categories[0]
        }

        this.runUpdates()
    }

    async getPrice(uuid = null) {
        const designerStore = useDesignerStore();

        let response = await this.axios.post( '/api/quote-builder/quote/layers?uuid=' + uuid , designerStore.selectedOptions)

        designerStore.setLayers(response.data.layers)
        designerStore.setPrice(response.data.price)
        designerStore.description = response.data.description
        designerStore.setDimensionFields(response.data.fields)
        designerStore.setSelectedOptions(response.data.options)
    }

    async loadImages() {
        const designerStore = useDesignerStore();

        let response = await this.axios.post(coreDesignerUrl + '/api/designer/v2/variant/images/' + designerStore.selectedVariant.id, {
            'selectedOptions':  designerStore.selectedOptions
        })

        designerStore.setImages(response.data.images)
    }

    async loadHelpArticle(layerId) {
        const designerStore = useDesignerStore();

        if (layerId !== null) {
            let response = await this.axios.post(coreDesignerUrl + '/api/designer/v2/variant/help/' + layerId, {
                'selectedOptions': designerStore.selectedOptions
            })

            designerStore.setHelp(response.data.article)
        } else {
            designerStore.setHelp(null)
        }

    }


    async runUpdates(layerId = null, leadGenUuid = null) {
        const designerStore = useDesignerStore();
        const coreStore = useCoreStore();

        if (coreStore.pageData.uuid) {
            leadGenUuid = coreStore.pageData.uuid
        }

        designerStore.variantLoading = true;

        await Promise.all([
            this.getPrice(leadGenUuid),
            this.loadHelpArticle(layerId)
        ]);

        // Images need to load last to ensure everything is correct
        this.loadImages();

        designerStore.variantLoading = false
    }

    async addToCart() {
        const designerStore = useDesignerStore();
        const coreStore = useCoreStore();
        let response;

        if (designerStore.editing) {
            response = await this.axios.post(
                '/api/quote-builder/quote/update-item/' + designerStore.itemId,
                {
                    raw_data: designerStore.selectedOptions
                }
            );
        } else {
            response = await this.axios.post(
                '/api/quote-builder/quote/design/' + coreStore.pageData.uuid,
                designerStore.selectedOptions
            );
        }

        if (response.status == 200) {
            designerStore.cartMessage = 'Item was successfully added to cart'
            designerStore.cartMessageType = 'add_cart'
            designerStore.addedToCart = true

        } else {
            designerStore.cartMessage = 'Unable to add to quote, please try again later or contact our support team'
            designerStore.cartMessageType = 'add_cart_fail'
        }

    }

    async loadFlow(uuid) {
        let response = await this.axios.get(  '/api/flows/find/' + uuid, {
            domain: window.location.origin.replace(/(^\w+:|^)\/\//, '')
        });

        if (response.data) {
            return response.data
        } else {
            return false
        }
    }

    async loadFlowStep(uuid, step) {
        let response = await this.axios.get(  '/api/flows/step/' + uuid + '/' + step, {
            domain: window.location.origin.replace(/(^\w+:|^)\/\//, '')
        });

        if (response.data) {
            return response.data
        } else {
            return false
        }
    }
}